/**
 * Resolve the platform catalog HealthcareServices used as Group Creation defaults.
 *
 * Read-only by design: the Group Therapy (90853) and Group Therapy Screening rows are
 * platform-wide catalog entries seeded by `npm run seed:service-catalog`. Practice-scoped
 * roles only hold read/search on HealthcareService, so creating or patching them from the
 * browser fails with Forbidden.
 */
import type { MedplumClient } from '@medplum/core';
import type { HealthcareService } from '@medplum/fhirtypes';
import {
  GROUP_THERAPY_SCREENING_CATALOG_KEY,
  GROUP_THERAPY_SESSION_CATALOG_KEY,
  HEALTHCARE_SERVICE_KEY_SYSTEM,
} from '@unify/fhir-model/constants';

export type GroupCatalogServiceRef = {
  reference: string;
  display: string;
  id: string;
};

export type ResolvedGroupHealthcareServices = {
  session?: GroupCatalogServiceRef;
  screening?: GroupCatalogServiceRef;
};

type ResolveSpec = {
  catalogKey: string;
  name: string;
};

const SESSION_SPEC: ResolveSpec = {
  catalogKey: GROUP_THERAPY_SESSION_CATALOG_KEY,
  name: 'Group Therapy',
};

const SCREENING_SPEC: ResolveSpec = {
  catalogKey: GROUP_THERAPY_SCREENING_CATALOG_KEY,
  name: 'Client screening',
};

function toRef(hs: HealthcareService, fallbackName: string): GroupCatalogServiceRef | undefined {
  if (!hs.id) return undefined;
  return {
    id: hs.id,
    reference: `HealthcareService/${hs.id}`,
    display: hs.name?.trim() || fallbackName,
  };
}

async function findByCatalogKey(
  medplum: MedplumClient,
  catalogKey: string,
): Promise<HealthcareService | undefined> {
  return medplum.searchOne('HealthcareService', {
    identifier: `${HEALTHCARE_SERVICE_KEY_SYSTEM}|${catalogKey}`,
    active: 'true',
  });
}

async function resolveOne(
  medplum: MedplumClient,
  spec: ResolveSpec,
): Promise<GroupCatalogServiceRef | undefined> {
  const hs = await findByCatalogKey(medplum, spec.catalogKey);
  return hs ? toRef(hs, spec.name) : undefined;
}

/**
 * Looks up the Group Therapy session + screening HealthcareServices and returns references
 * suitable for Group setup form defaults. Either may be undefined when the platform catalog
 * has not been seeded — callers should leave the field for the user to pick.
 */
export async function resolveGroupTherapyHealthcareServices(
  medplum: MedplumClient,
): Promise<ResolvedGroupHealthcareServices> {
  const [session, screening] = await Promise.all([
    resolveOne(medplum, SESSION_SPEC),
    resolveOne(medplum, SCREENING_SPEC),
  ]);
  return { session, screening };
}
